import React, { useState } from 'react';
import SideNavbar from '../../../components/SideNavbar';
import ImageToPDF from '../../../components/tools/ImageToPDF';
import MergePDF from '../../../components/tools/MergePDF';
import SplitPDF from '../../../components/tools/SplitPDF';
import EMICalculator from '../../../components/tools/EMICalculator';
import CurrencyConverter from '../../../components/tools/CurrencyConverter';


// redux
import { useAppSelector } from "../../../redux/store";

type Tool = {
  id: string;
  name: string;
  description: string;
  icon: string;
};

const availableTools: Tool[] = [
  {
    id: 'image-to-pdf',
    name: 'Image to PDF',
    description: 'Convert multiple images into a single PDF file',
    icon: 'ImageToPdf.svg'
  },
  {
    id: 'merge-pdf',
    name: 'Merge PDFs',
    description: 'Combine multiple PDF files into a single document',
    icon: 'MergePdf.svg'
  },
  {
    id: 'split-pdf',
    name: 'Split PDF',
    description: 'Extract specific pages from a PDF document',
    icon: 'SplitPdf.svg'
  },
  {
    id: 'emi-calculator',
    name: 'EMI Calculator',
    description: 'Calculate EMI, interest and view amortization schedule in INR',
    icon: 'Calculator.svg'
  },
  {
    id: 'currency-converter',
    name: 'Currency Converter',
    description: 'Convert any amount between currencies using real-time exchange rates quickly and easily.',
    icon: 'CurrencyConverter.svg'
  }
];

const Tools = () => {
  const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);

  const renderSelectedTool = () => {
    switch (selectedTool) {
      case 'image-to-pdf':
        return <ImageToPDF />;
      case 'merge-pdf':
        return <MergePDF />;
      case 'split-pdf':
        return <SplitPDF />;
      case 'emi-calculator':
        return <EMICalculator />;
      case 'currency-converter':
        return <CurrencyConverter />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-200">
      <div className="fixed top-16 left-0 h-full z-10">
        <SideNavbar />
      </div>

      <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
        <div className="bg-white p-6 rounded-3xl shadow-md w-full">
          <div className="mb-10">
            <div className="flex items-center gap-6 mb-6">
              <div
                className="flex items-center justify-center w-14 h-14 rounded-full"
                style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)', }}>
                <img className='w-8 h-8' src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/tools-white.svg`} alt="Tools Icon" />
              </div>
              <h2 className="text-2xl font-medium text-black"
                style={{ fontFamily: 'Montserrat', fontSize: '24px', fontWeight: '500', lineHeight: '29.26px', textAlign: 'left', }}>
                Tools
              </h2>
            </div>
            <div className="h-px bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200"></div>
          </div>
          {!selectedTool ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {availableTools.map((tool) => (
                <div
                  key={tool.id}
                  onClick={() => setSelectedTool(tool.id)}
                  className="bg-white rounded-lg p-6 border border-gray-200 cursor-pointer hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/desktop/${tool.icon}`}
                      alt={tool.name}
                      className="w-8 h-8"
                    />
                    <span className="text-xl font-montserrat font-semibold">{tool.name}</span>
                  </div>
                  <p className="text-gray-600">{tool.description}</p>
                </div>
              ))}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-6 border border-gray-200 opacity-75">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <span className="text-xl font-montserrat font-semibold text-gray-400">More Tools</span>
                </div>
                <p className="text-gray-400">Stay tuned! More exciting tools are coming soon to enhance your productivity.</p>
              </div>
            </div>
          ) : (
            <div>
              <button
                onClick={() => setSelectedTool(null)}
                className="mb-6 inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
              >
                <span className="mr-2">←</span>
                Back to Tools
              </button>
              {renderSelectedTool()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tools;